import { useCallback, useEffect, useRef, useState } from 'react'
import { addWeeks, format } from 'date-fns'
import { useIntl } from 'react-intl'
import { getMemberPtoSuggestions } from '../services/api'
import { PTOSuggestionsResponse } from '../../EnergyLevelPage/types'

const DATE_FMT = 'yyyy-MM-dd'
const DEFAULT_LIMIT = 3
const LOOKAHEAD_WEEKS = 8

export interface IUseMemberPtoSuggestions {
  data: PTOSuggestionsResponse | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useMemberPtoSuggestions(userId: string | undefined): IUseMemberPtoSuggestions {
  const { formatMessage } = useIntl()
  const [data, setData] = useState<PTOSuggestionsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const tokenRef = useRef(0)

  const fetchFor = useCallback(
    async (id: string) => {
      const token = ++tokenRef.current
      setLoading(true)
      setError(null)
      try {
        const from = addWeeks(new Date(), 1)
        const to = addWeeks(from, LOOKAHEAD_WEEKS)
        const response = await getMemberPtoSuggestions({
          userId: id,
          fromDate: format(from, DATE_FMT),
          toDate: format(to, DATE_FMT),
          limit: DEFAULT_LIMIT,
        })
        if (token !== tokenRef.current) return
        setData(response)
      } catch {
        if (token !== tokenRef.current) return
        setData(null)
        setError(formatMessage({ id: 'teamEnergy.memberDetail.suggestionsLoadError' }))
      } finally {
        if (token === tokenRef.current) setLoading(false)
      }
    },
    [formatMessage]
  )

  useEffect(() => {
    if (!userId) {
      tokenRef.current++
      setData(null)
      setError(null)
      setLoading(false)
      return
    }
    fetchFor(userId)
  }, [userId, fetchFor])

  const refetch = useCallback(async () => {
    if (userId) await fetchFor(userId)
  }, [userId, fetchFor])

  return { data, loading, error, refetch }
}
